import React, { createContext, useContext, useState, ReactNode, useMemo } from "react";
import FinOpsCostBreakupDrawer from "components/FineOps/FinOpsCostBreakupDrawer";
import { getFinopsCostBreakupDetails } from "service/service";
import HighlightedText from "components/GlobalSearch/HighlightText";
type FinOpsDrawerContextType = {
  openDrawer: (title: string) => void;
  closeDrawer: () => void;
};

export const FinOpsDrawerContext = createContext<FinOpsDrawerContextType | undefined>(undefined);

const platformTitleToApiParamMap: Record<string, string> = {
		'Azure Cost': 'Azure',
		'Data Visualization Platform Cost': 'Visualization',
		'Integration (Informatica) Platform Cost': 'Integration',
		'Data Virtualization Platform Cost': 'Virtualization',
		'Other Platforms Cost (YTD)': 'Other Platforms',
		'OnPrem Vs Cloud Cost': 'onPrem Vs Cloud'
	};

export const useFinOpsDrawer = () => {
  const ctx = useContext(FinOpsDrawerContext);
  if (!ctx) throw new Error("useFinOpsDrawer must be used inside FinOpsDrawerProvider");
  return ctx;
};

export const FinOpsDrawerProvider = ({ children }: { children: ReactNode }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState("");
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [breakupData, setBreakupData] = useState<any[]>([]);

  const transformCostBreakupData = (apiData: any) => {
    if (!apiData) return [];
    const lmcgMap = Object.fromEntries(
      apiData?.lmcg?.map((item: any) => [item.type, item.avgGrowthPct]) || []
    );
    const costDetailList = apiData?.costDetail || [];
    if (costDetailList.length === 0) {
      return ["Software", "Hardware", "Labor"].map((type) => ({
        title: `${type} Cost`,
        value: 0,
        change: lmcgMap[type] ?? "-",
      }));
    }
    return costDetailList.map((item: any) => ({
      title: `${item.type} Cost`,
      value: item.costYTD ?? 0,
      change: lmcgMap[item.type] ?? "-",
    }));
  };

  const openDrawer = async (title: string) => {
    console.log("FinOpsDrawerContext - title*************:", title);
    setIsDrawerOpen(true);
    setDrawerTitle(title);
    setDrawerLoading(true);
    try {
      const param = platformTitleToApiParamMap[title] || title;
      const res = await getFinopsCostBreakupDetails(param);
      if (res.success) {
        setBreakupData(transformCostBreakupData(res.data));
      }
    } catch (err) {
      console.error("Drawer fetch error:", err);
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setBreakupData([]);
  };

  const contextValue = useMemo(
    () => ({
      openDrawer,
      closeDrawer,
    }),
    [openDrawer, closeDrawer]
  );
      
  return (
    <FinOpsDrawerContext.Provider value={contextValue}>
      {children}
      <FinOpsCostBreakupDrawer
        open={isDrawerOpen}
        onClose={closeDrawer}
        title={<HighlightedText text={`${drawerTitle} Breakups`} />}
        data={breakupData}
        loading={drawerLoading}
      />
    </FinOpsDrawerContext.Provider>
  );
};
